import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Input, Textarea } from "@/components/ui";
import { SettingsPanel } from "./SettingsSection";

export const ACCENT_COLOR_PRESETS = [
  { hex: "#5a8a72", label: "Sage" },
  { hex: "#5b7ea8", label: "Steel" },
  { hex: "#8b7aad", label: "Lavender" },
  { hex: "#b86e6e", label: "Rose" },
  { hex: "#c08a5a", label: "Amber" },
  { hex: "#5a9ea8", label: "Teal" },
  { hex: "#6e6da0", label: "Indigo" },
] as const;

export const INDUSTRY_OPTIONS = [
  "Financial Services",
  "Healthcare",
  "Manufacturing",
  "Technology",
  "Retail",
  "Energy",
  "Government",
  "Education",
  "Other",
];

export const COMPANY_SIZE_OPTIONS = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001-5000",
  "5000+",
];

export const JURISDICTION_OPTIONS = [
  "United States",
  "European Union",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Japan",
  "Other",
];

interface WorkspaceSettingsFormProps {
  workspace: {
    _id: Id<"workspaces">;
    name: string;
    slug: string;
    role: string;
    industry: string;
    companySize: string;
    jurisdiction: string;
    companyDescription?: string;
    accentColor?: string;
  };
}

export function WorkspaceSettingsForm({
  workspace,
}: WorkspaceSettingsFormProps) {
  const { t } = useTranslation("settings");
  const updateWorkspace = useMutation(api.workspaces.update);
  const isReadOnly = workspace.role === "member";

  const [name, setName] = useState(workspace.name);
  const [industry, setIndustry] = useState(workspace.industry);
  const [companySize, setCompanySize] = useState(workspace.companySize);
  const [jurisdiction, setJurisdiction] = useState(workspace.jurisdiction);
  const [companyDescription, setCompanyDescription] = useState(
    workspace.companyDescription ?? "",
  );
  const [accentColor, setAccentColor] = useState(workspace.accentColor ?? "");
  const [saving, setSaving] = useState(false);

  const isDirty =
    name !== workspace.name ||
    industry !== workspace.industry ||
    companySize !== workspace.companySize ||
    jurisdiction !== workspace.jurisdiction ||
    companyDescription !== (workspace.companyDescription ?? "") ||
    accentColor !== (workspace.accentColor ?? "");

  async function handleSave() {
    if (!isDirty || isReadOnly) return;
    setSaving(true);
    try {
      console.log("Saving workspace settings", workspace);
      await updateWorkspace({
        workspaceId: workspace._id,
        name: name !== workspace.name ? name : undefined,
        industry: industry !== workspace.industry ? industry : undefined,
        companySize:
          companySize !== workspace.companySize ? companySize : undefined,
        jurisdiction:
          jurisdiction !== workspace.jurisdiction ? jurisdiction : undefined,
        companyDescription:
          companyDescription !== (workspace.companyDescription ?? "")
            ? companyDescription || undefined
            : undefined,
        accentColor:
          accentColor !== (workspace.accentColor ?? "")
            ? accentColor || undefined
            : undefined,
      });
      toast.success(t("workspace.formActions.saved"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("workspace.formActions.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  }

  const selectClassName =
    "h-9 w-full rounded-button border border-border bg-card px-2.5 text-[14px] text-primary outline-none cursor-pointer hover:bg-card-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <SettingsPanel>
      <div className="max-w-md space-y-4 mb-6">
        <div>
          <span className="block text-[13px] font-medium text-primary mb-1.5">
            {t("workspace.fields.workspaceName")}
          </span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isReadOnly}
          />
        </div>

        <div>
          <span className="block text-[13px] font-medium text-primary mb-1.5">
            {t("workspace.fields.workspaceUrl")}
          </span>
          <div className="flex items-center h-9 rounded-button border border-border bg-page px-3 text-[14px] text-secondary">
            {workspace.slug}
          </div>
        </div>

        <div>
          <label className="block">
            <span className="block text-[13px] font-medium text-primary mb-1.5">
              {t("workspace.fields.industry")}
            </span>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              disabled={isReadOnly}
              className={selectClassName}
            >
              {INDUSTRY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <label className="block">
            <span className="block text-[13px] font-medium text-primary mb-1.5">
              {t("workspace.fields.companySize")}
            </span>
            <select
              value={companySize}
              onChange={(e) => setCompanySize(e.target.value)}
              disabled={isReadOnly}
              className={selectClassName}
            >
              {COMPANY_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <label className="block">
            <span className="block text-[13px] font-medium text-primary mb-1.5">
              {t("workspace.fields.jurisdiction")}
            </span>
            <select
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              disabled={isReadOnly}
              className={selectClassName}
            >
              {JURISDICTION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <span className="block text-[13px] font-medium text-primary mb-1.5">
            {t("workspace.fields.companyDescription")}
          </span>
          <p className="text-xs text-secondary mb-1.5">
            {t("workspace.fields.companyDescriptionHelp")}
          </p>
          <Textarea
            value={companyDescription}
            onChange={(e) => {
              if (e.target.value.length <= 500) {
                setCompanyDescription(e.target.value);
              }
            }}
            placeholder={t("workspace.fields.companyDescriptionPlaceholder")}
            rows={3}
            disabled={isReadOnly}
          />
          <p className="text-xs text-muted text-right mt-1">
            {companyDescription.length}/500
          </p>
        </div>

        <div>
          <span className="block text-[13px] font-medium text-primary mb-1.5">
            {t("workspace.fields.accentColor")}
          </span>
          <div className="flex items-center gap-2">
            {ACCENT_COLOR_PRESETS.map(({ hex, label }) => {
              const isSelected = accentColor === hex;
              return (
                <button
                  key={hex}
                  type="button"
                  title={label}
                  disabled={isReadOnly}
                  onClick={() => setAccentColor(hex)}
                  className="relative w-8 h-8 rounded-full border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: hex,
                    borderColor: isSelected ? hex : "transparent",
                    boxShadow: isSelected
                      ? `0 0 0 2px var(--color-page), 0 0 0 4px ${hex}`
                      : undefined,
                  }}
                >
                  {isSelected && (
                    <Check className="absolute inset-0 m-auto w-4 h-4 text-white" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {!isReadOnly && (
        <div className="flex justify-end max-w-md">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!isDirty}
            loading={saving}
          >
            {t("workspace.formActions.saveChanges")}
          </Button>
        </div>
      )}
    </SettingsPanel>
  );
}
